import {Component, EventEmitter, HostListener, Input, OnInit, Output, ViewChild} from '@angular/core';
import {Template} from "../../../model/template";
import {DeviceMeasurement} from "../../../model/device-measurement";
import {DeviceProperty} from "../../../model/device-property";
import {AssetMeasurement} from "../../../model/asset-measurement";
import {AssetProperty} from "../../../model/asset-property";
import {ActivatedRoute, Router} from "@angular/router";
import {CommonMessagesService} from "../../../services/common-messages.service";
import {TemplatesService} from "../../../services/templates.service";
import {TemplatesFormInfoComponent} from "./templates-form-info/templates-form-info.component";
import {TemplatePersistRequest} from "../../../model/requests/template-persist-request";
import {IntegrationModule} from "../../../model/integration-module";

@Component({
  selector: 'app-templates-form',
  templateUrl: './templates-form.component.html',
  styleUrls: ['./templates-form.component.scss']
})
export class TemplatesFormComponent implements OnInit {

  @Input() active: boolean;
  @Output() activeChange = new EventEmitter();

  items: any[];
  currentStep: number;
  stepChanged: boolean;

  template: Template;

  deviceMeasurements: DeviceMeasurement[];
  deviceExpressions: DeviceMeasurement[];
  deviceProperties: DeviceProperty[];
  integrationModules: IntegrationModule[];
  assetMeasurements: AssetMeasurement[];
  assetProperties: AssetProperty[];

  persistingChanges: boolean;
  formLoaded: boolean;
  formSuccess: boolean;

  mode: 'create' | 'edit';

  @ViewChild(TemplatesFormInfoComponent)
  templatesFormInfo: TemplatesFormInfoComponent;

  constructor(private templatesService: TemplatesService,
              private route: ActivatedRoute,
              private router: Router,
              private commonMessagesService: CommonMessagesService) {
    this.template = new Template();
    this.deviceMeasurements = [];
    this.deviceProperties = [];
    this.deviceExpressions = [];
    this.integrationModules = [];
    this.assetMeasurements = [];
    this.assetProperties = [];
    this.formSuccess = false;
    this.stepChanged = false;
  }

  ngOnInit(): void {
    this.currentStep = 0;
    this.items = [
      {index: 0, icon: 'pi pi-list', label: 'model.template', description: 'forms.descriptions.template'},
      {index: 1, icon: 'pi pi-wifi', label: 'model.sensors', description: 'forms.descriptions.templateSensors'},
      {index: 2, icon: 'pi pi-bars', label: 'model.properties', description: 'forms.descriptions.templateProperties'},
      {index: 3, icon: 'pi pi-check', label: 'forms.confirmation'}
    ];

    this.route.url.subscribe(urlSegment => {
      const firstPath = urlSegment[0].path;
      if (firstPath === 'new') {
        this.mode = 'create';
        this.loadCreationForm();
      } else {
        this.mode = 'edit';
        this.loadEditionForm(firstPath);
      }
    });
  }

  loadCreationForm(): void {
    this.template = new Template();
    this.formLoaded = true;
  }

  loadEditionForm(uuid): void {
    this.templatesService.getTemplate(uuid).toPromise().then(response => {
      this.template = response.template;
      if (this.template.content?.deviceMeasurements) {
        this.deviceMeasurements = this.template.content.deviceMeasurements.filter(measurement => !measurement.expression);
        this.deviceExpressions = this.template.content.deviceMeasurements.filter(measurement => measurement.expression);
      } else {
        this.deviceMeasurements = [];
        this.deviceExpressions = [];
      }
      this.deviceProperties = this.template.content?.deviceProperties ? this.template.content.deviceProperties : [];
      this.integrationModules = this.template.content?.integrationModules ? this.template.content.integrationModules : [];
      this.assetMeasurements = this.template.content?.assetMeasurements ? this.template.content.assetMeasurements : [];
      this.assetProperties = this.template.content?.assetProperties ? this.template.content.assetProperties : [];
      this.loadItems(this.template.type);
      this.formLoaded = true;
    }, error => {
      if (error.status === 404) {
        this.router.navigate(['/not-found']);
      }
    });
  }

  persistTemplateData(): void {
    this.persistingChanges = true;
    const persistData = this.buildPersistData();

    let promise;
    if (this.mode === 'create') {
      promise = this.templatesService.createTemplate(persistData).toPromise();
    } else {
      promise = this.templatesService.editTemplate(persistData).toPromise();
    }

    promise.then(response => {
      if (this.active) {
        this.active = false;
        this.activeChange.emit(this.active);
      }
      this.formSuccess = true;
      this.router.navigate(([`/system/templates/${this.mode === 'create' ? response.uuid : persistData.uuid}/details`]));
    }).catch(error => {
      this.commonMessagesService.showPersistingError(this.mode, error, persistData.name);
      this.persistingChanges = false;
    });
  }

  onNextStep(): void {
    this.stepChanged = true;
    if (this.currentStep < this.items.length - 1) {
      this.currentStep++;
    }
  }

  onPreviousStep(): void {
    this.stepChanged = true;
    if (this.currentStep > 0) {
      this.currentStep--;
    }
  }

  tenantChanged(): void {
    this.resetData();
  }

  typeChanged(type: string): void {
    this.loadItems(type);
    this.resetData();
  }

  private loadItems(type: string) {
    if (type === 'DEVICE') {
      this.items = [
        {index: 0, icon: 'pi pi-list', label: 'model.template', description: 'forms.descriptions.template'},
        {index: 1, icon: 'pi pi-wifi', label: 'model.sensors', description: 'forms.descriptions.templateSensors'},
        {index: 2, icon: 'pi pi-bars', label: 'model.expressions', description: 'forms.descriptions.templateExpressions'},
        {index: 3, icon: 'pi pi-bars', label: 'model.properties', description: 'forms.descriptions.templateProperties'},
        {index: 4, icon: 'pi pi-table', label: 'model.integrationModules', description: 'forms.descriptions.templateIntegrationModules'},
        {index: 5, icon: 'pi pi-check', label: 'forms.confirmation'}
      ];
    } else {
      this.items = [
        {index: 0, icon: 'pi pi-list', label: 'model.template', description: 'forms.descriptions.template'},
        {index: 1, icon: 'pi pi-wifi', label: 'model.sensors', description: 'forms.descriptions.templateSensors'},
        {index: 2, icon: 'pi pi-bars', label: 'model.properties', description: 'forms.descriptions.templateProperties'},
        {index: 3, icon: 'pi pi-check', label: 'forms.confirmation'}
      ];
    }
  }

  private resetData(): void {
    this.deviceMeasurements = [];
    this.deviceProperties = [];
    this.integrationModules = [];
    this.assetMeasurements = [];
    this.assetProperties = [];
  }

  private buildPersistData(): TemplatePersistRequest {
    return {
      name: this.template.name,
      description: this.template.description,
      type: this.template.type,
      tenantUuid: this.template.tenant.uuid,
      deviceCategory: this.template.deviceCategory,
      deviceMeasurements: this.deviceMeasurements.concat(this.deviceExpressions),
      deviceProperties: this.deviceProperties,
      integrationModules: this.integrationModules,
      assetMeasurements: this.assetMeasurements,
      assetProperties: this.assetProperties,
      uuid: this.mode === 'edit' ? this.template.uuid : null
    };
  }

  onCancel(route: string): Promise<boolean> {
    if (route == null) {
      return this.router.navigate([`/system/templates`]);
    }
    return this.router.navigate([`${route}`]);
  }

  @HostListener('window:beforeunload')
  canDeactivate(): boolean {
    if (this.formSuccess) {
      return true;
    }
    return !this.stepChanged && (this.currentStep === 0 && this.templatesFormInfo.templateForm.untouched);
  }
}
